import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import {
  useQuiz,
  useQuizAttempt,
  useQuizQuestions,
} from '@/hooks/useQuizzes'
import { QuizQuestion } from '@/types/quizzes'
import { QuizAnswerPreview } from '@/components/quizzes/QuizAnswerPreview'
import { paths } from '@/routes/path'
import { labelForQuizQuestionType, labelForQuizAttemptStatus } from '@/utils/quiz'

export function LearnerQuizAttemptDetailPage() {
  const { courseId, quizId, attemptId } = useParams<{
    courseId: string
    quizId: string
    attemptId: string
  }>()
  const navigate = useNavigate()

  const { data: quiz, isLoading: isQuizLoading } = useQuiz(quizId)
  const { data: attempt, isLoading: isAttemptLoading, error: attemptError } = useQuizAttempt(
    quizId,
    attemptId,
  )
  const { data: quizQuestions, isLoading: isQuestionsLoading } = useQuizQuestions(quizId)

  const questionMap = useMemo(() => {
    const questions = quizQuestions?.questions ?? quiz?.questions ?? []
    return questions.reduce<Record<string, QuizQuestion>>((acc, question) => {
      acc[question.id] = question
      return acc
    }, {})
  }, [quizQuestions, quiz])

  if (!quizId || !attemptId) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Attempt not found</CardTitle>
            <CardDescription className="text-sm text-red-600">
              We could not locate this quiz attempt. Return to your course and open the quiz again.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (isQuizLoading || isAttemptLoading || isQuestionsLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (attemptError || !attempt || !quiz) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Unable to load attempt</CardTitle>
            <CardDescription className="text-sm text-red-600">
              This attempt may not exist or you might not have permission to view it.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const answers = attempt.answers?.questions ?? []

  return (
    <div className="space-y-6">
      <header className="space-y-3 border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            className="gap-2 p-0 text-sm text-gray-600"
            onClick={() => navigate(paths.app.quizzes.courseList.build(courseId ?? ''), { replace: true })}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to quizzes
          </Button>
          <div className="flex items-center gap-2">
            <Badge variant="outline">Attempt {attempt.attempt}</Badge>
            <Badge variant="secondary">{labelForQuizAttemptStatus(attempt.status)}</Badge>
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">{quiz.title}</h1>
        <p className="text-gray-600">
          Review your responses and any feedback from your instructor.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
          <CardDescription>
            Submitted {attempt.submittedAt ? new Date(attempt.submittedAt).toLocaleString() : 'Not yet submitted'}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <SummaryItem
            label="Auto score"
            value={
              attempt.autoScore !== null && attempt.autoScore !== undefined
                ? attempt.autoScore.toFixed(2)
                : 'Not available'
            }
          />
          <SummaryItem
            label="Manual score"
            value={
              attempt.manualScore !== null && attempt.manualScore !== undefined
                ? attempt.manualScore.toFixed(2)
                : 'Not graded yet'
            }
          />
          <SummaryItem
            label="Time taken"
            value={
              attempt.durationSeconds
                ? `${Math.round(attempt.durationSeconds / 60)} minutes`
                : 'Unknown'
            }
          />
        </CardContent>
      </Card>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Question-by-question review</h2>
        {answers.map((entry, index) => {
          const question = questionMap[entry.id]
          const scoreRecord = attempt.scores?.find((score) => score.questionId === entry.id)
          return (
            <Card key={entry.id} className="border border-gray-200">
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-base">
                  <span>
                    Question {index + 1}{' '}
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      {question ? labelForQuizQuestionType(question.type) : 'Unknown type'}
                    </span>
                  </span>
                  <Badge variant="outline">{question?.scoreWeight ?? 0} pts</Badge>
                </CardTitle>
                <CardDescription className="flex flex-wrap items-center gap-2 text-sm">
                  <span>
                    Auto score:{' '}
                    <strong>{scoreRecord?.autoScore ?? 'N/A'}</strong>
                  </span>
                  <span>
                    Manual score:{' '}
                    <strong>{scoreRecord?.manualScore ?? 'Not graded'}</strong>
                  </span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-900">{question?.questionText ?? 'Question details unavailable.'}</p>
                <QuizAnswerPreview question={question} answer={entry.answer} files={attempt.files ?? []} />
                {scoreRecord?.feedback && (
                  <div className="rounded border border-green-100 bg-green-50 px-3 py-2 text-sm text-green-700">
                    <CheckCircle2 className="mr-2 inline h-4 w-4" />
                    {scoreRecord.feedback}
                  </div>
                )}
              </CardContent>
            </Card>
          )
        })}
      </section>
    </div>
  )
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-sm text-gray-800">{value}</p>
    </div>
  )
}
